import { format } from "date-fns";
import type { IconType } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import type { CoverageCellState, CoverageViewQuery } from "#graphql/supergraphTypes";

/** GraphQL `Date` scalars are ISO `YYYY-MM-DD` strings. Parse/serialize them in
 *  local time so the displayed quarter-end never drifts across a TZ boundary. */
export function isoToDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function dateToIso(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** True iff `s` is a strict `YYYY-MM-DD` calendar date (not just superficially
 *  ISO-shaped). Rejects month/day-out-of-range strings and non-numeric parts —
 *  used to defensively validate a URL-provided coverage date range before
 *  handing it to the grid query. */
export function isValidIsoDate(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return false;
  }
  const [y, m, d] = s.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
}

/** Default range the matrix opens on: start of the prior quarter through the
 *  first day after the current quarter ends (exclusive upper bound). Computed
 *  from `now` so the default never goes stale — a user opening the grid in any
 *  quarter sees that quarter plus the one before it, which covers the typical
 *  "what just closed and what's in flight" review window. */
export function getDefaultCoverageRange(now: Date = new Date()): {
  start: string;
  end: string;
} {
  const year = now.getFullYear();
  const currentQuarter = Math.floor(now.getMonth() / 3); // 0..3
  const startQuarter = currentQuarter - 1;
  const startYear = startQuarter < 0 ? year - 1 : year;
  const startMonth = startQuarter < 0 ? 9 : startQuarter * 3;
  const endQuarter = currentQuarter + 1;
  const endYear = endQuarter > 3 ? year + 1 : year;
  const endMonth = endQuarter > 3 ? 0 : endQuarter * 3;
  return {
    start: dateToIso(new Date(startYear, startMonth, 1)),
    end: dateToIso(new Date(endYear, endMonth, 1)),
  };
}

export function formatReportingDate(iso: string): string {
  return format(isoToDate(iso), "MMM d, yyyy");
}

export type CoverageQueryRow = CoverageViewQuery["coverageView"]["rows"][number];

export const WITHHELD_CELL_STATE = "WITHHELD";

/** The generated states this package gives a treatment, named explicitly.
 *
 *  `Extract` is doing real work in both directions. It ties these names to the
 *  generated union, so a rename or removal upstream turns a member into `never`
 *  and fails the presentation maps at compile time. It does NOT pick up a newly
 *  ADDED generated state, which is the point: fay can ship a state before this
 *  build's codegen has seen it, and an addition must not break the build. A new
 *  generated state falls to the unknown branch at runtime and surfaces in the
 *  schema-drift test instead. */
type TreatedGeneratedState = Extract<
  CoverageCellState,
  | "ALL_CHECKS_PASSED"
  | "REQUIRES_REVIEW"
  | "CONFIRMED_ISSUES"
  | "PROCESSING"
  | "MISSING_TARGET_DOC"
  | "EMPTY"
  | "EMPTY_PACKAGE"
>;

/** Plus the state fay resolves that the generated union does not carry yet.
 *  Collapses to `TreatedGeneratedState` once the supergraph recomposes, with no
 *  edit here: the literal is already a member of the generated union by then. */
export type RenderedCoverageCellState = TreatedGeneratedState | typeof WITHHELD_CELL_STATE;

/** `(string & {})` keeps the rendered literals in completions while still
 *  admitting a state fay emits ahead of this build's next deploy. */
export type CoverageCellStateWire = RenderedCoverageCellState | (string & {});

/** How each canonical state renders in a matrix cell. `icon: null` (EMPTY) draws
 *  a blank cell. Colors are DS tokens — no raw hex. */
export type CoverageCellPresentation = {
  icon: IconType | null;
  iconColor?: Colors;
  /** DS color token (string-valued) applied as the cell background. */
  background?: string;
  label: string;
};

/** One treatment on both flag paths: a variation would leak the state it exists
 *  to hide. Its own token pair — `MISSING_TARGET_DOC` reads as the opposite. */
export const WITHHELD_CELL_PRESENTATION: CoverageCellPresentation = {
  icon: "ellipsis",
  iconColor: Colors.Neutral700,
  background: Colors.Neutral300,
  label: "In progress",
};

// Labels for the four review states are the waterfall's own names, shared with
// the slideout's Status stat (PACKAGE_STATUS_PRESENTATION) so a cell and the
// stat behind it never describe the same package differently.
export const COVERAGE_CELL_PRESENTATION: Record<
  RenderedCoverageCellState,
  CoverageCellPresentation
> = {
  ALL_CHECKS_PASSED: {
    icon: "circle-check",
    iconColor: Colors.Success600,
    background: Colors.Success100,
    label: "No issues",
  },
  REQUIRES_REVIEW: {
    icon: "exclamation-triangle",
    iconColor: Colors.Warning700,
    background: Colors.Warning200,
    label: "Requires review",
  },
  // Red, and distinct from the amber REQUIRES_REVIEW: every finding has been
  // triaged and at least one is a confirmed issue. Green must never hide
  // confirmed issues.
  CONFIRMED_ISSUES: {
    icon: "exclamation-triangle",
    iconColor: Colors.Danger600,
    background: Colors.Danger200,
    label: "Confirmed issues",
  },
  PROCESSING: {
    icon: "clock",
    iconColor: Colors.Informative600,
    background: Colors.Informative200,
    label: "Checks running",
  },
  MISSING_TARGET_DOC: {
    icon: "file-exclamation",
    iconColor: Colors.Neutral600,
    background: Colors.Neutral200,
    label: "Missing",
  },
  EMPTY: {
    icon: null,
    background: Colors.Neutral100,
    label: "Not received",
  },
  EMPTY_PACKAGE: {
    icon: null,
    background: Colors.Neutral100,
    label: "Empty package",
  },
  WITHHELD: WITHHELD_CELL_PRESENTATION,
};

/** The treated states, taken from the map itself rather than a parallel list,
 *  so the map is the single source of truth. The map is typed as a total
 *  `Record<RenderedCoverageCellState, …>`, so these keys are exactly the type. */
const RENDERED_CELL_STATE_KEYS: ReadonlySet<string> = new Set(
  Object.keys(COVERAGE_CELL_PRESENTATION),
);

/** The wire boundary: every state entering this module goes through here, so an
 *  unrecognised value becomes `undefined` exactly once and every consumer has a
 *  single case to handle. Callers render nothing (`CoverageGrid`) or the blank
 *  fallback (`getCellPresentation`) — never an unchecked map index. */
function isRenderedCellState(value: unknown): value is RenderedCoverageCellState {
  return typeof value === "string" && RENDERED_CELL_STATE_KEYS.has(value);
}

export function toRenderedCellState(value: unknown): RenderedCoverageCellState | undefined {
  return isRenderedCellState(value) ? value : undefined;
}

/** Blank cell for an unrecognised state, so a render never destructures
 *  `undefined`. */
export const UNKNOWN_CELL_PRESENTATION: CoverageCellPresentation = {
  icon: null,
  label: "Unknown",
};

/** Pre-`fayMVPAugust` presentation. `CONFIRMED_ISSUES` deliberately collapses to
 *  the green `ALL_CHECKS_PASSED` reading: fay ships the state unconditionally,
 *  and a flag-off arena must see the exact pre-flag behavior. */
const LEGACY_COVERAGE_CELL_PRESENTATION: Record<
  RenderedCoverageCellState,
  CoverageCellPresentation
> = {
  ALL_CHECKS_PASSED: {
    icon: "circle-check",
    iconColor: Colors.Success600,
    background: Colors.Success100,
    label: "No issues",
  },
  REQUIRES_REVIEW: {
    icon: "exclamation-triangle",
    iconColor: Colors.Warning700,
    background: Colors.Warning200,
    label: "Has issues",
  },
  PROCESSING: {
    icon: "clock",
    iconColor: Colors.Informative600,
    background: Colors.Informative200,
    label: "Processing",
  },
  MISSING_TARGET_DOC: {
    icon: "file-exclamation",
    iconColor: Colors.Neutral600,
    background: Colors.Neutral200,
    label: "Missing",
  },
  EMPTY: {
    icon: null,
    background: Colors.Neutral100,
    label: "Not received",
  },
  EMPTY_PACKAGE: {
    icon: null,
    background: Colors.Neutral100,
    label: "Empty package",
  },
  CONFIRMED_ISSUES: {
    icon: "circle-check",
    iconColor: Colors.Success600,
    background: Colors.Success100,
    label: "No issues",
  },
  WITHHELD: WITHHELD_CELL_PRESENTATION,
};

/** Every presentation read goes through here so an unrecognised wire value
 *  cannot bypass the blank fallback. */
export function getCellPresentation(
  state: CoverageCellStateWire | undefined,
  fayMVPAugust: boolean,
): CoverageCellPresentation {
  const rendered = toRenderedCellState(state);
  if (rendered === undefined) {
    return UNKNOWN_CELL_PRESENTATION;
  }
  return fayMVPAugust
    ? COVERAGE_CELL_PRESENTATION[rendered]
    : LEGACY_COVERAGE_CELL_PRESENTATION[rendered];
}

// `value` is the generated enum, not the wider rendered union: only states the
// deployed supergraph accepts as filter input belong here.
export type CoverageStatusOption = { value: CoverageCellState; label: string };

// MISSING_TARGET_DOC, EMPTY, and EMPTY_PACKAGE render in cells but are
// deliberately not filterable. Ordered by review priority, not the waterfall's
// derivation order: PROCESSING is a state you wait out, not act on.
//
// WITHHELD is rendered but NOT filterable, and must stay that way until fay
// emits it. This package ships first, so the deployed supergraph's input enum
// has no WITHHELD member: sending it as a `CoverageFilter.states` variable
// fails GraphQL coercion and takes the whole grid query down with it. Add the
// option in a follow-up, once the enum is live.
export const COVERAGE_STATUS_OPTIONS: CoverageStatusOption[] = [
  { value: "REQUIRES_REVIEW", label: COVERAGE_CELL_PRESENTATION.REQUIRES_REVIEW.label },
  { value: "CONFIRMED_ISSUES", label: COVERAGE_CELL_PRESENTATION.CONFIRMED_ISSUES.label },
  { value: "ALL_CHECKS_PASSED", label: COVERAGE_CELL_PRESENTATION.ALL_CHECKS_PASSED.label },
  { value: "PROCESSING", label: COVERAGE_CELL_PRESENTATION.PROCESSING.label },
];

/** The original 3-state set: no CONFIRMED_ISSUES, legacy labels. */
const LEGACY_COVERAGE_STATUS_OPTIONS: CoverageStatusOption[] = [
  {
    value: "ALL_CHECKS_PASSED",
    label: LEGACY_COVERAGE_CELL_PRESENTATION.ALL_CHECKS_PASSED.label,
  },
  { value: "REQUIRES_REVIEW", label: LEGACY_COVERAGE_CELL_PRESENTATION.REQUIRES_REVIEW.label },
  { value: "PROCESSING", label: LEGACY_COVERAGE_CELL_PRESENTATION.PROCESSING.label },
];

export function getCoverageStatusOptions(fayMVPAugust: boolean): CoverageStatusOption[] {
  return fayMVPAugust ? COVERAGE_STATUS_OPTIONS : LEGACY_COVERAGE_STATUS_OPTIONS;
}

/** Only the states the filter offers reach the query, and every one of those is
 *  in the generated input enum — so this needs no assertion. `undefined` for an
 *  empty selection: the query reads an explicit `[]` as "match no cell", which
 *  is not what an unfiltered grid means. */
export function toCoverageFilterStates(
  statuses: readonly CoverageCellStateWire[],
): CoverageCellState[] | undefined {
  const selected = new Set<string>(statuses);
  const states = COVERAGE_STATUS_OPTIONS.filter((option) => selected.has(option.value)).map(
    (option) => option.value,
  );
  return states.length ? states : undefined;
}

/** Flat AG Grid row, one per fund. Period states are keyed by their ISO date so
 *  a date column can resolve its value via `field: <iso>`. The drill-down
 *  package id for each period is stored under a prefixed key (`packageFieldFor`)
 *  so it travels with the row without colliding with the ISO-date state columns. */
export type CoverageRowData = {
  fundId: string;
  fundName: string;
  /** Mirrors the fund's `isFaLocked`. Explains the row to the reviewer; it is
   *  not a permission signal and does not change cell presentation. */
  isFaManaged: boolean;
} & Record<string, string | boolean | null>;

/** Row key holding a cell's drill-down package id, parallel to the ISO-date
 *  state column. Prefixed so it never collides with a real period `field`. */
export function packageFieldFor(period: string): string {
  return `pkg::${period}`;
}

/** Builds AG Grid row data as a flat fund list — one row per fund, with no
 *  family grouping. Ownership-tree roots are kept and render as ordinary fund
 *  rows alongside everything else, so any fund a package can be bound to shows
 *  up here. Cell states are spread onto each fund row keyed by ISO period. */
export function buildCoverageRows(rows: readonly CoverageQueryRow[]): CoverageRowData[] {
  return rows.map((row) => {
    const data: CoverageRowData = {
      fundId: row.fund.id,
      fundName: row.fund.name || row.fund.id,
      // Nullable on the wire; null means "unknown", which is not FA-managed.
      isFaManaged: row.fund.isFaLocked === true,
    };
    for (const cell of row.cells) {
      data[cell.period] = cell.state;
      data[packageFieldFor(cell.period)] = cell.cellId ?? null;
    }
    return data;
  });
}

/** The date columns the grid renders, as sorted unique ISO strings. Starts from
 *  the backend-provided `periods` and folds in every reporting date actually
 *  present in the rows, so a package whose date is not a standard quarter-end
 *  (e.g. a mid-quarter Jun 11) still gets its own column rather than being
 *  dropped. ISO `YYYY-MM-DD` sorts lexicographically == chronologically. */
export function computePeriodColumns(
  periods: readonly string[],
  rows: readonly CoverageQueryRow[],
): string[] {
  const columns = new Set<string>(periods);
  for (const row of rows) {
    for (const cell of row.cells) {
      columns.add(cell.period);
    }
  }
  return Array.from(columns).sort();
}
